import React, { useEffect, useState } from 'react';

export interface MenuFilterCategory {
  id: number;
  type: string;
}

export interface MenuFilterCuisine {
  id: number;
  name: string;
}

export interface MobileMenuFilterOverlayProps {
  categories: MenuFilterCategory[];
  selectedCategories: number[];
  cuisines: MenuFilterCuisine[];
  tags: string[];
  selectedCuisines: number[];
  selectedTags: string[];
  itemType: string;
  priceRange: [number, number];
  priceSliderMax: number;
  formatPrice: (price: number) => string;
  sortBy: string;
  showWishlist: boolean;
  hideSavedTab?: boolean;
  onWishlistChange: (enabled: boolean) => void;
  onCategoryToggle: (id: number) => void;
  onCuisineToggle: (id: number) => void;
  onTagToggle: (tag: string) => void;
  onItemTypeToggle: (type: 'veg' | 'nonveg') => void;
  onPriceRangeChange: (range: [number, number]) => void;
  onSortChange: (sortOption: string) => void;
  onClear: () => void;
  onClose: () => void;
  onApply: () => void;
}

const MobileMenuFilterOverlay: React.FC<MobileMenuFilterOverlayProps> = ({
  categories,
  selectedCategories,
  cuisines,
  tags,
  selectedCuisines,
  selectedTags,
  itemType,
  priceRange,
  priceSliderMax,
  formatPrice,
  sortBy,
  showWishlist,
  hideSavedTab = false,
  onWishlistChange,
  onCategoryToggle,
  onCuisineToggle,
  onTagToggle,
  onItemTypeToggle,
  onPriceRangeChange,
  onSortChange,
  onClear,
  onClose,
  onApply,
}) => {
  const [activeTab, setActiveTab] = useState<
    'saved' | 'category' | 'cuisine' | 'tags' | 'price' | 'sort' | 'diet'
  >(hideSavedTab ? 'diet' : 'saved');

  const hasCategories = categories && categories.length > 0;
  const hasCuisines = cuisines && cuisines.length > 0;
  const hasTags = tags && tags.length > 0;

  useEffect(() => {
    if (activeTab === 'category' && !hasCategories) {
      if (hasCuisines) setActiveTab('cuisine');
      else if (hasTags) setActiveTab('tags');
      else setActiveTab('diet');
    }
  }, [hasCategories, hasCuisines, hasTags, activeTab]);

  return (
    <div className="mobile-filter-overlay drawer-overlay open" onClick={onClose} role="presentation">
      <div
        className="mobile-filter-content drawer open"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
      >
        <div className="drawer-handle" />
        <div className="mobile-filter-header drawer-header">
          <h3>Filters</h3>
          <button type="button" className="close-filter-btn drawer-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="mobile-filter-body drawer-body">
          <div className="mobile-filter-tabs drawer-tabs">
            {!hideSavedTab ? (
              <button
                type="button"
                className={`filter-tab drawer-tab ${activeTab === 'saved' ? 'active' : ''}`}
                onClick={() => setActiveTab('saved')}
              >
                Saved
              </button>
            ) : null}
            {hasCategories && (
              <button
                type="button"
                className={`filter-tab drawer-tab ${activeTab === 'category' ? 'active' : ''}`}
                onClick={() => setActiveTab('category')}
              >
                Category
              </button>
            )}
            <button
              type="button"
              className={`filter-tab drawer-tab ${activeTab === 'diet' ? 'active' : ''}`}
              onClick={() => setActiveTab('diet')}
            >
              Diet
            </button>
            {hasCuisines && (
              <button
                type="button"
                className={`filter-tab drawer-tab ${activeTab === 'cuisine' ? 'active' : ''}`}
                onClick={() => setActiveTab('cuisine')}
              >
                Cuisine
              </button>
            )}
            {hasTags && (
              <button
                type="button"
                className={`filter-tab drawer-tab ${activeTab === 'tags' ? 'active' : ''}`}
                onClick={() => setActiveTab('tags')}
              >
                Tags
              </button>
            )}
            <button
              type="button"
              className={`filter-tab drawer-tab ${activeTab === 'price' ? 'active' : ''}`}
              onClick={() => setActiveTab('price')}
            >
              Price
            </button>
            <button
              type="button"
              className={`filter-tab drawer-tab ${activeTab === 'sort' ? 'active' : ''}`}
              onClick={() => setActiveTab('sort')}
            >
              Sort
            </button>
          </div>

          <div className="mobile-filter-content-area drawer-panel">
            {!hideSavedTab && activeTab === 'saved' && (
              <div className="filter-section">
                <label className={`checkbox-item ${showWishlist ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={showWishlist}
                    onChange={(e) => onWishlistChange(e.target.checked)}
                  />
                  <span className="custom-check" />
                  Show saved items only
                </label>
                <p className="filter-section-hint">
                  View dishes you bookmarked. Sign in is required to see your saved list.
                </p>
              </div>
            )}

            {activeTab === 'category' && hasCategories && (
              <div className="filter-section">
                {categories
                  .filter((c) => c?.id && c.type?.trim())
                  .map((cat) => (
                    <label
                      key={cat.id}
                      className={`checkbox-item ${selectedCategories.includes(cat.id) ? 'active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.id)}
                        onChange={() => onCategoryToggle(cat.id)}
                      />
                      <span className="custom-check" />
                      {cat.type}
                    </label>
                  ))}
              </div>
            )}

            {activeTab === 'diet' && (
              <div className="filter-section">
                <label className={`checkbox-item ${itemType === 'veg' ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={itemType === 'veg'}
                    onChange={() => onItemTypeToggle('veg')}
                  />
                  <span className="custom-check" />
                  Vegetarian
                </label>
                <label className={`checkbox-item ${itemType === 'nonveg' ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={itemType === 'nonveg'}
                    onChange={() => onItemTypeToggle('nonveg')}
                  />
                  <span className="custom-check" />
                  Non Vegetarian
                </label>
              </div>
            )}

            {activeTab === 'cuisine' && hasCuisines && (
              <div className="filter-section">
                {cuisines
                  .filter((c) => c?.id && c.name?.trim())
                  .map((cuisine) => (
                    <label
                      key={cuisine.id}
                      className={`checkbox-item ${selectedCuisines.includes(cuisine.id) ? 'active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCuisines.includes(cuisine.id)}
                        onChange={() => onCuisineToggle(cuisine.id)}
                      />
                      <span className="custom-check" />
                      {cuisine.name}
                    </label>
                  ))}
              </div>
            )}

            {activeTab === 'tags' && hasTags && (
              <div className="filter-section">
                <div className="tag-list">
                  {tags
                    .filter((tag) => tag?.trim())
                    .map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className={`tag-pill ${selectedTags.includes(tag) ? 'active' : ''}`}
                        onClick={() => onTagToggle(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {activeTab === 'price' && (
              <div className="filter-section">
                <div className="price-range-slider">
                  <input
                    type="range"
                    min={0}
                    max={priceSliderMax}
                    value={priceRange[1]}
                    onChange={(e) => onPriceRangeChange([0, Number(e.target.value)])}
                    className="price-slider"
                    aria-label="Maximum price"
                  />
                  <div className="price-range-label">Up to {formatPrice(priceRange[1])}</div>
                </div>
              </div>
            )}

            {activeTab === 'sort' && (
              <div className="filter-section">
                {[
                  { value: 'default', label: 'Default' },
                  { value: 'price-asc', label: 'Price: Low to High' },
                  { value: 'price-desc', label: 'Price: High to Low' },
                  { value: 'rating', label: 'Rating' },
                  { value: 'name-asc', label: 'Name: A to Z' },
                  { value: 'name-desc', label: 'Name: Z to A' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`checkbox-item ${sortBy === opt.value ? 'active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="sort"
                      checked={sortBy === opt.value}
                      onChange={() => onSortChange(opt.value)}
                    />
                    <span className="custom-check custom-check--radio" />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mobile-filter-footer drawer-footer">
          <button type="button" className="filter-action-btn btn-clear" onClick={onClear}>
            Clear
          </button>
          <button
            type="button"
            className="filter-action-btn primary btn-apply"
            onClick={() => {
              onApply();
              onClose();
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileMenuFilterOverlay;
